/**
 * The browser's image work for a photo staff add in Teslyn Cloud (decisions 427, 429): decode it (turned upright by
 * its EXIF orientation), then draw the display size and the thumbnail as JPEG with the sizes and qualities of
 * `PHOTO_RENDITIONS`. Behind {@link PhotoRenderer} so the screen tests inject a fake (jsdom has no canvas). The two
 * sizes are drawn from the pixels, so they carry no EXIF (no GPS for clients). A bitmap is kept as a PNG, pixel for
 * pixel (R144, `image-import.ts`): {@link PhotoRenderer.toPng} draws it at its own size.
 */
import { fitWithin, PHOTO_RENDITIONS } from '@teslyn/domain';

export interface RenderedPhoto {
  /** The photo's own size in pixels, upright. */
  readonly width: number;
  readonly height: number;
  /** JPEG, 2048 px on the long edge (never enlarged). */
  readonly display: Blob;
  /** JPEG, 400 px on the long edge. */
  readonly thumb: Blob;
}

export interface PhotoRenderer {
  render(file: Blob): Promise<RenderedPhoto>;
  /** The picture as a PNG at its own size (a bitmap chosen as a photo, R144). */
  toPng(file: Blob): Promise<Blob>;
}

type Canvas2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

/** The pixel colour behind a transparent PNG or WebP once it is a JPEG (image content, not interface colour). */
const JPEG_BACKGROUND = 'white';

const UNREADABLE = 'The browser could not read this photo. Save it again as a JPEG and add it again.';

async function draw(
  bitmap: ImageBitmap,
  width: number,
  height: number,
  type: 'image/jpeg' | 'image/png',
  quality?: number,
): Promise<Blob> {
  let ctx: Canvas2D | null;
  let toBlob: () => Promise<Blob | null>;
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    ctx = canvas.getContext('2d');
    toBlob = () => canvas.convertToBlob(quality === undefined ? { type } : { type, quality });
  } else {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    ctx = canvas.getContext('2d');
    toBlob = () => new Promise((ok) => canvas.toBlob(ok, type, quality));
  }
  if (!ctx) throw new Error(UNREADABLE);
  if (type === 'image/jpeg') {
    ctx.fillStyle = JPEG_BACKGROUND;
    ctx.fillRect(0, 0, width, height);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, width, height);
  const blob = await toBlob();
  if (!blob) throw new Error(UNREADABLE);
  return blob;
}

async function drawJpeg(bitmap: ImageBitmap, maxPx: number, quality: number): Promise<Blob> {
  const { width, height } = fitWithin(bitmap.width, bitmap.height, maxPx);
  return draw(bitmap, width, height, 'image/jpeg', quality);
}

async function decode(file: Blob): Promise<ImageBitmap> {
  if (typeof createImageBitmap !== 'function') throw new Error(UNREADABLE);
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new Error(UNREADABLE);
  }
}

/** The renderer of the running browser: createImageBitmap and a canvas (OffscreenCanvas where there is one). */
export const browserPhotoRenderer: PhotoRenderer = {
  async render(file: Blob): Promise<RenderedPhoto> {
    const bitmap = await decode(file);
    try {
      const display = await drawJpeg(
        bitmap,
        PHOTO_RENDITIONS.display.maxPx,
        PHOTO_RENDITIONS.display.quality,
      );
      const thumb = await drawJpeg(bitmap, PHOTO_RENDITIONS.thumb.maxPx, PHOTO_RENDITIONS.thumb.quality);
      return { width: bitmap.width, height: bitmap.height, display, thumb };
    } finally {
      bitmap.close();
    }
  },
  async toPng(file: Blob): Promise<Blob> {
    const bitmap = await decode(file);
    try {
      return await draw(bitmap, bitmap.width, bitmap.height, 'image/png');
    } finally {
      bitmap.close();
    }
  },
};
